import { useState, useEffect } from 'react';
import { useAuth } from '@erpsquad/common/runtime';
import { auth } from '../constants/auth';
import { getV1CompanyId } from '../api-client/api.rbac/api';

/**
 * Resolves the effective user from either the AuthContext (host app) or
 * localStorage (MFE mode where the AuthContext is not bridged).
 */
const getEffectiveUser = (contextUser: any): any => {
    if (contextUser) return contextUser;
    try {
        const stored = window.localStorage.getItem(auth.storageUserKeyName);
        return stored ? JSON.parse(stored) : null;
    } catch {
        return null;
    }
};

/**
 * Fetches the company's `country_code` from the DB via GET /v1/company/:id
 * and returns it as a lowercase ISO-3166-1 alpha-2 string (e.g. "ae", "sa").
 *
 * Falls back to "ae" when:
 *  - The user / company_id is not yet available
 *  - The company record has no country_code set
 *  - The API call fails
 */
export function useCompanyCountryCode(): { countryCode: string; loading: boolean } {
    const { user: authUser } = useAuth();
    const [countryCode, setCountryCode] = useState<string>('ae');
    const [loading, setLoading] = useState<boolean>(false);

    useEffect(() => {
        const user = getEffectiveUser(authUser);
        const companyId = user?.company_id;
        if (!companyId) return;

        const token = window.localStorage.getItem(auth.storageTokenKeyName) ?? undefined;

        setLoading(true);
        getV1CompanyId({ id: String(companyId), 'x-token': token })
            .then((res: any) => {
                const code: string | undefined = res?.data?.company?.data?.country_code;
                if (code) setCountryCode(code.toLowerCase());
            })
            .catch(() => {
                // keep the default 'ae'
            })
            .finally(() => setLoading(false));
    // Re-run only when the resolved company_id changes
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [authUser]);

    return { countryCode, loading };
}
